<template>
    <div class="program-component">
        <card title="SIG Administration Categories">
            <form>
                <h6>Add Category</h6>
                <b-col>
                    <b-row class="mb-2">
                        <fg-input class="mt-2" placeholder="Category Name (e.g., Breakfast)"
                            v-model="newCategory.categoryName" />
                        <fg-input class="ml-2 mt-2" placeholder="Start Time (e.g., 07:00)"
                            v-model="newCategory.startTime" type="time" />
                        <fg-input class="ml-2 mt-2" placeholder="End Time (e.g., 10:00)"
                            v-model="newCategory.endTime" type="time" />
                    </b-row>
                    <b-row class="mb-2">
                        <b-form-select class="ml-2 mt-2" v-model="newCategory.programId">
                            <b-form-select-option :value="null">All Programs</b-form-select-option>
                            <b-form-select-option v-for="program in filteredPrograms" :key="program.id"
                                :value="program.id">
                                {{ program.name }}
                            </b-form-select-option>
                        </b-form-select>
                    </b-row>
                    <b-row>
                        <b-button class="mt-2" size="sm" variant="success" @click="addCategory">Add Category</b-button>
                    </b-row>
                </b-col>

                <h6 class="mt-4">Existing Categories</h6>
                <b-table :items="categories" :fields="fields" :per-page="20" :current-page="currentPage">

                    <template #cell(programId)="row">
                        {{ getProgramName(row.item.programId) }}
                    </template>
                    <template #cell(options)="row">
                        <b-button variant="danger" size="sm" @click="removeCategory(row.item.id)">Delete</b-button>
                    </template>
                </b-table>
                <BottomPagerBar v-model="currentPage" :total-rows="categories.length" :per-page="20" show-total />

                <h6 class="mt-4">Test Time Mapping</h6>
                <b-row>
                    <b-col md="4">
                        <fg-input v-model="testTime" placeholder="Enter time (e.g., 08:30)" type="time"></fg-input>
                    </b-col>
                    <b-col md="4">
                        <b-button variant="info" @click="testTimeMapping">Test Mapping</b-button>
                    </b-col>
                    <b-col md="4">
                        <div v-if="testResult">
                            <strong>Result:</strong> {{ testResult }}
                        </div>
                    </b-col>
                </b-row>
            </form>
        </card>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Axios from 'axios';
import { Component } from 'vue-property-decorator';
import { SigAdministrationCategory } from "@/models/SigAdministrationCategory";
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: 'SigAdministrationCategoriesPage',
    components: {
        BottomPagerBar,
    },
})
export default class SigAdministrationCategoriesPage extends Vue {
    protected categories: Array<SigAdministrationCategory> = [];
    private newCategory: SigAdministrationCategory = {} as SigAdministrationCategory;
    protected currentPage = 1;
    private programs: Array<any> = [];
    private testTime: string = "";
    protected testResult: string = "";

    protected fields: any[] = [
        {
            key: "categoryName",
            label: "Category",
        },
        {
            key: "startTime",
            label: "Start Time",
            formatter: this.timeFormatter,
        },
        {
            key: "endTime",
            label: "End Time",
            formatter: this.timeFormatter,
        },

        {
            key: "programId",
            label: "Program",
        },
        {
            key: "options",
            label: "Actions",
        },
    ];

    get filteredPrograms() {
        return this.programs;
    }

    created() {
        this.getCategories();
        this.loadPrograms();
    }

    getCategories() {
        Axios.get('SigAdministrationCategories/')
             .then(response => {
                 this.categories = response.data;
             })
             .catch(error => {
                 console.error(error);
                 this.$notification(NotificationOptions.error("Failed to load categories"));
             });
    }

    timeFormatter(value: string) {
        const hour = Number(value.substring(0, 2));
        const minute = Number(value.substring(3, 5)).toString().padStart(2, "0");
        const ampm = Number(value.substring(0, 2)) < 12 ? "AM" : "PM";
        let hourVal = hour < 12 ? hour : hour - 12;
        hourVal = hourVal == 0 ? 12 : hourVal;
        return hourVal + ":" + minute + " " + ampm + "";
    }

    loadPrograms() {
        Axios.get('api/Program/')
             .then(response => {
                 this.programs = response.data;
             })
             .catch(error => {
                 console.error(error);
             });
    }

    addCategory() {
        if (this.newCategory.categoryName && this.newCategory.startTime && this.newCategory.endTime) {
            Axios.post('api/SigAdministrationCategories/Save', {
                    CategoryName: this.newCategory.categoryName,
                    StartTime: this.newCategory.startTime + ":00",
                    EndTime: this.newCategory.endTime + ":00",
                    ProgramId: this.newCategory.programId || null,
                },
            )
                 .then(_ => {
                     this.getCategories();
                     this.$notification(NotificationOptions.success("Category Added"));
                     this.newCategory = {} as SigAdministrationCategory;
                 })
                 .catch(error => {
                     console.error(error);
                     this.$notification(NotificationOptions.error(error));
                 });
        } else {
            this.$notification(NotificationOptions.error("Please fill all required fields"));
        }
    }

    removeCategory(categoryId: number) {
        Axios.post(`SigAdministrationCategories/Remove/${categoryId}`)
             .then(() => {
                 this.getCategories();
                 this.$notification(NotificationOptions.success("Category Removed"));
             })
             .catch(error => {
                 this.$notification(NotificationOptions.error(error));
             });
    }

    testTimeMapping() {
        if (!this.testTime) {
            this.$notification(NotificationOptions.error("Please enter a time to test"));
            return;
        }
        const programId = this.newCategory.programId || null;

        Axios.get('api/SigAdministrationCategories/MapTimeToCategory', {
            params: {
                time: this.testTime,
                programId,
            },
        })
             .then(response => {
                 if (response.data.category) {
                     this.testResult = `Maps to: ${response.data.category}`;
                 } else {
                     this.testResult = "No category found for this time";
                 }
             })
             .catch(error => {
                 console.error(error);
                 this.$notification(NotificationOptions.error("Failed to test time mapping"));
             });
    }

    getProgramName(programId: number | null): string {
        if (!programId) return "";
        const program = this.programs.find(p => p.id === programId);
        return program ? program.name : `Program ${programId}`;
    }
}
</script>

<style scoped></style>
